import styles from '../Style/LayerOverview.module.scss';
import {
  DeleteOutlined,
  DownOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  UploadOutlined,
  UpOutlined,
} from '@ant-design/icons';
import {
  Button,
  Divider,
  Modal,
  Radio,
  Tooltip,
  Typography,
} from 'antd';
import React, { useContext, useState, useEffect, useMemo, useRef } from 'react';
import { StoreContext } from '../../../store/store';
import Api from '../../../api/Api';
import { Legend } from '@amcharts/amcharts4/charts';
import Upload, { UploadProps } from 'antd/lib/upload';
import guid from '../../../generic/Guid';
import { IPolygonLayer, IUploadedLayer } from '../../../components/Map/Layer/DatasetZoneLayer';
import { Position, RGBAColor } from 'deck.gl';
import { ConvertHexToRGBA, ConvertRGBtoHex, THOWDWAVESPECTRA } from '../../../generic/common';
import { useCallback } from 'react';
import { ColorResult, HuePicker } from 'react-color';
import { UploadFile } from 'antd/lib/upload/interface';
import LoadingContext from '../../../context/loadingContext/LoadingContext';
import { withLoading } from '../../../components/Loading/withLoading';
import { IFeature } from '../../../api/FileEndPoint';
import { ILegend, overlay } from '../../../typings/home';
import IconPoint from '../../../assets/icon/locate2.png'

export interface LayerOverviewProps {
  onMeshControl: (value: string) => any;
  result: any;
  setResult: any;
  legendStartEndValues: [number | null, number | null],
  setLegendStartEndValues : Function
}

export default function LayerOverview(props: LayerOverviewProps) {
  const {
    markerCollections,
    setMarkerCollections,
    showDatasetZones,
    setShowDatasetZones,
    mapBoundsZoom,
    setLegend,
    setShowUploadedShapefile,
    uploadedFeatures,
    setUploadedFeatures,
    uploadingShapefile,
    setUploadingShapefile,
    visibleUploads,
    setVisibleUploads,
    showLabelLayer,
    setShowLabelLayer,
    fwePoints,
    inSituPoints,
    showFWEPoints,
    setShowFWEPoints,
    showInSituPoints,
    setShowInSituPoints,
    showGlobalShapeFile,
    showGlobalTextShapeFile,
    setShowGlobalTextShapeFile,
    setShowGlobalShapeFile,
    showMesh,
    setShowMesh,
    showSurfaceMap,
    setShowSurfaceMap
  } = useContext(StoreContext);
  const loadingContext = useContext(LoadingContext);
  const [showSurfaceMapSection, setShowSurfaceMapSection] = useState(true);
  const [showPointSection, setShowPointsSection] = useState(true);
  const [showLayerSection, setShowLayerSection] = useState(true)
  const [showMenuOWF, setShowMenuOWF] = useState(false)
  const prevShowSurfaceMapRef = useRef(false);

  const { result, setResult, legendStartEndValues } = props

  let filesToUpload = new Array<File>();
  const point = markerCollections.find((x) => x.active);

  const { bounds, zoom, index } = useMemo(() => {
    const getApiIndex = (zoomLevel: number) => {
      let i = 5;
      switch (true) {
        case zoomLevel < 5:
          i = 1;
          break;
        case zoomLevel >= 5 && zoomLevel < 7:
          i = 2;
          break;
        case zoomLevel >= 7 && zoomLevel < 9:
          i = 3;
          break;
        case zoomLevel >= 9 && zoomLevel < 11:
          i = 4;
          break;
      }
      return i;
    };
    return { bounds: mapBoundsZoom.bounds, zoom: mapBoundsZoom.zoom, index: getApiIndex(mapBoundsZoom.zoom) }
  }, [mapBoundsZoom])

  const updateShowLabels = (state: boolean) => {
    setShowLabelLayer(state);
  };

  const updateMeshSwitch = (state: boolean) => {
    if (point !== undefined) {
      setShowMesh(state);
      setMarkerCollections([...markerCollections]);
      if (state === true) props.onMeshControl(point.dataset);
      else props.onMeshControl('');
    }
  };

  const updateEnableSurfaceMap = async (state: boolean) => {
    setShowSurfaceMap(state)
    if (!state) {
      point!.surfaceMaps.selectedLevel2 = '';
      setLegend({ ...Legend, display: state });
    }
    setMarkerCollections([...markerCollections]);
  };

  const handleSurfaceMap = (levels: string, fromEffect: boolean) => {
    if (levels === 'none') {
      updateEnableSurfaceMap(false);
      return;
    }

    if (!fromEffect && !showSurfaceMap) {
      updateEnableSurfaceMap(true);
    }

    const selectedLevels = levels.split('|');
    if (selectedLevels.length > 1 && point && point.surfaceMaps) {
      point.surfaceMaps.selectedLevel1 = selectedLevels[0];
      point.surfaceMaps.selectedLevel2 = selectedLevels[1];
      setMarkerCollections([...markerCollections]);
    }
  };

  useEffect(() => {
    const overlay = point?.surfaceMaps.overlays.find(
      (x) => x.Level1 === point?.surfaceMaps.selectedLevel1 && x.Level2 === point?.surfaceMaps.selectedLevel2
    );

    if (overlay && result) {
      const data = new Array<IPolygonLayer>();
      const legend = {
        title: 'Legend',
        name: overlay.Level1,
        unit: overlay.Unit,
        display: true,
        values: [],
      } as ILegend;
      if (result.featureCollection && result.featureCollection.features && result.featureCollection.features.length > 0) {
        const minValue = legendStartEndValues[0] === null ? result.minDepth : legendStartEndValues[0];
        const maxValue = legendStartEndValues[1] === null ? result.maxDepth : legendStartEndValues[1];
        const range = (maxValue - minValue) / overlay.LegendColor.length;

        // "AB" adds 67% opacity (https://gist.github.com/lopspower/03fb1cc0ac9f32ef38f4)
        for (let i = 1; i <= overlay.LegendColor.length; i++) {
          legend.values.push({
            color: overlay.LegendColor[i - 1], //AddTransparencyToHexColor(overlay.LegendColor[i - 1], 'AB'),
            value: minValue + range * i,
          });
        }

        (result.featureCollection.features as IFeature[]).forEach((feature) => {
          const index = [];
          for (const x in feature.properties) {
            index.push(x);
          }
          const propertyValue = feature.properties[index[0]];
          let legendItem = legend.values.find(
            (x) => x.value >= propertyValue
          )
          if (legendItem === undefined) {
            legendItem = legend.values[legend.values.length - 1]
          }
          const color: string = legendItem !== undefined ? legendItem.color : '';

          (feature.geometry.coordinates as Position[][]).forEach((polygon) => {
            data.push({
              id: guid(),
              coordinates: polygon,
              lineColor: ConvertHexToRGBA(color, 0),
              color: ConvertHexToRGBA(color), //, 170), // 170 adds 67% (170 of 255) opacity  (https://www.blitzrechner.de/prozent/)
              value: propertyValue.toFixed(2).toString(),
            });
          });
        });
      }

      overlay!.data = data;
      const hasData = overlay && overlay.data && overlay.data.length > 0;
      const title = legend.name;
      setLegend({
        ...legend,
        display: hasData,
        title: title,
        name: overlay.Level2,
      });
      setMarkerCollections([...markerCollections]);
    }

  }, [result, legendStartEndValues]);

  const updateSurfaceMap = async (
    level1: string,
    level2: string,
    bounds: number[],
    apiIndex: number
  ) => {
    const surfaceMap = point?.surfaceMaps.overlays.find(
      (x) => x.Level1 === level1 && x.Level2 === level2
    );
    const response = await Api.fileEndPoint.getLayGeoJson(
      apiIndex,
      bounds,
      point!.dataset,
      surfaceMap!
    );
    setResult(response)
  };

  useEffect(() => {
    const prevShowSurfaceMap = prevShowSurfaceMapRef.current;
    prevShowSurfaceMapRef.current = showSurfaceMap;

    if (
      showSurfaceMap &&
      prevShowSurfaceMap &&
      point &&
      point.surfaceMaps &&
      !point.surfaceMaps.selectedLevel2
    ) {
      if (point.surfaceMaps.categories.length > 0) {
        const firstCategory = point.surfaceMaps.categories[0];
        const firstOverlay = point.surfaceMaps.overlays.find(
          (o) => o.Level1 === firstCategory.name
        );
        handleSurfaceMap(
          firstOverlay !== undefined
            ? firstOverlay.Level1 + '|' + firstOverlay.Level2
            : '',
          true
        );
      }
    }
  }, [point?.surfaceMaps, showSurfaceMap]);

  useEffect(() => {
    if (bounds[0] === 0 && bounds[1] === 0 && bounds[2] === 0 && bounds[3] === 0 && zoom === 1) {
      return;
    }
    if (
      point &&
      point.surfaceMaps &&
      point.surfaceMaps.selectedLevel1 &&
      point.surfaceMaps.selectedLevel2
    ) {
      updateSurfaceMap(
        point!.surfaceMaps.selectedLevel1,
        point!.surfaceMaps.selectedLevel2,
        bounds,
        index
      );
    }
  }, [
    bounds,
    zoom,
    index,
    point &&
    point.surfaceMaps &&
    point.surfaceMaps.selectedLevel1 &&
    point.surfaceMaps.selectedLevel2
  ]);

  const updateShowShape = async (
    state: boolean,
    id: string,
    level1: string,
    level2: string,
    color: string
  ) => {
    const shape = point?.shapeFiles.find(
      (x) => x.Level1 === level1 && x.Level2 === level2
    );
    shape!.IsDisplay = state;
    setMarkerCollections([...markerCollections]);

    if (state && shape?.data === undefined) {
      shape!.data = await Api.fileEndPoint.getOverlayGeoJson(
        id,
        level1,
        level2,
        color
      );
      setMarkerCollections([...markerCollections]);
    }
  };

  const checkShapefileToUpload = (files: File[]) => {
    if (files.length !== 3) {
      return false;
    }
    const fullNames = files.map((file: File) => file.name.split('.'));
    const names = fullNames.map((fullName: string[]) => {
      return fullName.length === 2 ? fullName[0] : '';
    });
    if (names.length !== 3) {
      return false;
    }
    if (names[0] !== names[1] || names[1] !== names[2]) {
      return false;
    }
    const extensions = fullNames.map((fullName: string[]) => fullName[1]);
    return (
      extensions.includes('shp') &&
      extensions.includes('shx') &&
      extensions.includes('prj')
    );
  };

  const getFileName = useCallback(
    (files: File[]) => {
      let name = '';
      if (files.length > 0) {
        const file = files[0];
        const fullName = file.name.split('.');
        if (fullName.length > 0) {
          name = fullName[0];
        }
      }

      // Ensure that name is unique
      const exisitingNames = uploadedFeatures.map(
        (layer: IUploadedLayer) => layer.id
      );
      if (exisitingNames.includes(name)) {
        let num = 1;
        while (
          exisitingNames.some((label) => label === name + ' (' + num + ')')
        ) {
          num++;
        }
        return name + ' (' + num + ')';
      }

      return name;
    },
    [uploadedFeatures]
  );

  const uploadFiles = async (file: File) => {
    filesToUpload.push(file);
    if (filesToUpload.length > 3) {
      filesToUpload = filesToUpload.splice(-3);
    }
    if (checkShapefileToUpload(filesToUpload)) {
      setUploadingShapefile(true);
      const name = getFileName(filesToUpload);
      try {
        const orderId = guid();

        await Api.fileEndPoint.uploadFile(
          'shape',
          3,
          orderId,
          filesToUpload
        ).catch((e) => {
          console.error(e);
        });

        let times = 0;
        const maxTimes = 10;
        const flag = setInterval(async () => {
          times++;
          try {
            const status = await Api.fileEndPoint.checkFileStatus(orderId);
            if (status === true || times === maxTimes) {
              clearInterval(flag);

              const geojson = await Api.fileEndPoint.getGeoJsonLayer(
                orderId,
                'FF5733'
              );
              if (geojson && geojson.features) {
                const color = [
                  Math.floor(Math.random() * 256),
                  Math.floor(Math.random() * 256),
                  Math.floor(Math.random() * 256),
                ] as RGBAColor;
                setUploadedFeatures((previous: Array<IUploadedLayer>) =>
                  previous.concat([
                    { id: name, features: geojson.features, color: color },
                  ])
                );
                setShowUploadedShapefile(true);
                setShowUploadList(false);
                setVisibleUploads((previous: Array<string>) => [
                  ...previous,
                  name,
                ]);
                setFileList([]);
              }
            }
          } finally {
            setUploadingShapefile(false);
          }
        }, 5000);
      } finally {
        filesToUpload = [];
      }
    } else {
      setUploadingShapefile(false);
    }
  };

  const handleOnUpload = () => {
    setShowUploadList(true);
  };

  const [showUploadList, setShowUploadList] = useState(false);
  const [fileList, setFileList] = useState<UploadFile<any>[]>([]);
  const uploadProps: UploadProps = {
    accept: '.shp, .shx, .prj',
    multiple: true,
    maxCount: 3,
    showUploadList: showUploadList,
    beforeUpload: (file) => {
      if (file !== undefined) {
        uploadFiles(file);
      }
      return false;
    },
    onChange(info) {
      setFileList(info.fileList);
      if (info.file.status === 'done') {
        setUploadingShapefile(false);
      } else if (info.file.status === 'error') {
        setUploadingShapefile(false);
      }
    },
  };

  const onChangeUploadVisibility = (layerId: string) => {
    const newVisibleUploads = visibleUploads.includes(layerId) ?
      visibleUploads.filter((id) => id !== layerId) : [...visibleUploads, layerId];
    setVisibleUploads(newVisibleUploads);

  };

  const deleteUpload = (layerId: string) => {
    setUploadedFeatures((previous: Array<IUploadedLayer>) =>
      previous.filter((layer: IUploadedLayer) => layer.id !== layerId)
    );
  };

  const [currentLayerColor, setCurrentLayerColor] = useState<
    string | undefined
  >(undefined);
  const [selectedLayerId, setSelectedLayerId] = useState<string | undefined>(
    undefined
  );

  const onCloseColorPickerDialog = () => {
    setSelectedLayerId(undefined);
    setCurrentLayerColor(undefined);
  };

  const applyColor = useCallback(
    (color: ColorResult) => {
      const newColor: RGBAColor = ConvertHexToRGBA(color.hex);
      setUploadedFeatures((previous: Array<IUploadedLayer>) =>
        previous.map((layer: IUploadedLayer) => {
          return layer.id === selectedLayerId
            ? { ...layer, color: newColor }
            : layer;
        })
      );
    },
    [selectedLayerId]
  );

  const changeColor = useCallback(
    (layer: IUploadedLayer) => {
      if (layer !== undefined) {
        setSelectedLayerId(layer.id);
        const color: string = ConvertRGBtoHex(layer.color);
        setCurrentLayerColor(color);
      }
    },
    [uploadedFeatures]
  );

  const isHave2DSpectra =  point?.shapeFiles.some(item => item.Level1 === "2D Wave Spectra" || item.Level2 === "2D Wave Spectra");
  const isHavePoint = point && point?.id !== undefined
  const isHaveFwePoint = fwePoints && fwePoints.length > 0
  const isHaveInsituPoint = inSituPoints && inSituPoints.length > 0

  return (
    <div className={`${styles.root}`}>
      <Modal
        visible={currentLayerColor !== undefined}
        title={'Select a color'}
        onCancel={onCloseColorPickerDialog}
        footer={<Button onClick={onCloseColorPickerDialog}>OK</Button>}
        style={{
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        <HuePicker
          color={currentLayerColor}
          onChange={(color) => setCurrentLayerColor(color.hex)}
          onChangeComplete={applyColor}
        />
      </Modal>

      {/* POINT LAYER SECTION */}
      <div className={`${styles.menuBox}`}>
        <div className={styles.wrapperLabel}>
          <div className={styles.menuLabel}>
            Points
          </div>
          {showPointSection ? (
            <UpOutlined
              onClick={() => setShowPointsSection(false)}
              className={styles.toggleArrow}
            />
            ) : (
              <DownOutlined
                onClick={() => setShowPointsSection(true)}
                className={styles.toggleArrow}
              />
            )}
        </div>
        {showPointSection && (
          <>
            <div className={`${styles.menuRow}`}>
              <div className={styles.iconLabel}>
                <img src={IconPoint} width={30} height={30} style={{position: 'relative', right: '6px'}} />
                <Typography style={{ color: !isHavePoint ? 'grey' : 'black', position: 'relative', right: '7px' }}>User Points</Typography>
              </div>
              <div className={styles.groupItem}>
                <div onClick={() => isHavePoint ? updateShowLabels(!showLabelLayer) : null} style={{ cursor: 'pointer', fontSize: '20px' }}>
                  {isHavePoint ? showLabelLayer ? <EyeOutlined /> : <EyeInvisibleOutlined /> : <EyeInvisibleOutlined disabled style={{ cursor: 'not-allowed' }} /> }
                </div>
              </div>
            </div>

            <div className={`${styles.menuRow}`}>
              <div className={styles.iconLabel}>
                <div className={styles.circleInSitu} />
                <Typography style={{ marginLeft: 5, color: !isHaveInsituPoint ? 'grey' : 'black' }}>In-Situ Station</Typography>
                </div>
              <div className={styles.groupItem}>
                <div onClick={() => isHaveInsituPoint ? setShowInSituPoints(!showInSituPoints) : null} style={{ cursor: 'pointer', fontSize: '20px' }}>
                {isHaveInsituPoint ? showInSituPoints ? <EyeOutlined /> : <EyeInvisibleOutlined /> : <EyeInvisibleOutlined disabled style={{ cursor: 'not-allowed' }} /> }
                </div>
              </div>
            </div>

            <div className={`${styles.menuRow}`}>
              <div className={styles.iconLabel}>
                <div className={styles.circle} />
                <Typography style={{ marginLeft: 5, color: !isHaveFwePoint ? 'grey' : 'black' }}>Local Model </Typography>
              </div>
              <div className={styles.groupItem}>
                <div onClick={() => isHaveFwePoint ? setShowFWEPoints(!showFWEPoints) : null} style={{ cursor: 'pointer', fontSize: '20px' }}>
                  {isHaveFwePoint ? showFWEPoints ? <EyeOutlined /> : <EyeInvisibleOutlined /> : <EyeInvisibleOutlined disabled style={{ cursor: 'not-allowed' }} /> }
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* LAYER SECTION */}
      <div className={`${styles.menuBox}`}>
        <div className={styles.wrapperLabel}>
          <div className={styles.menuLabel}>
            Layers
          </div>
          {showLayerSection ? (
            <UpOutlined
            onClick={() => setShowLayerSection(false)}
            className={styles.toggleArrow}
            />
            ) : (
              <DownOutlined
              onClick={() => setShowLayerSection(true)}
              className={styles.toggleArrow}
              />
            )}
        </div>
       {showLayerSection && (
          <>
            <div className={`${styles.menuRow}`}>
              <div className={styles.iconLabel}>
                <div className={styles.boxPerimeter} />
                <Typography style={{ marginLeft: 5}}>Model Perimeter</Typography>
              </div>
              <div className={styles.groupItem}>
                <div onClick={() => setShowDatasetZones(!showDatasetZones)} style={{ cursor: 'pointer', fontSize: '20px' }}>
                  {showDatasetZones ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                </div>
              </div>
            </div>
            <div className={`${styles.menuRow}`}>
              <div className={styles.iconLabel}>
              <div className={styles.boxGrid} />
              <Typography style={{ marginLeft: 5, color: !isHavePoint ? 'grey' : 'black' }}>Model Grid</Typography>
              </div>
              <div className={styles.groupItem}>
                <div
                  onClick={() => withLoading(() => isHavePoint ? updateMeshSwitch(!showMesh) : null, loadingContext, {
                      source: 'meshSwitch',
                  })}
                  style={{ cursor: 'pointer', fontSize: '20px' }}
                >
                  {isHavePoint ? showMesh ? <EyeOutlined /> : <EyeInvisibleOutlined /> : <EyeInvisibleOutlined disabled style={{ cursor: 'not-allowed' }} /> }
                  </div>
              </div>
            </div>
            {point && point?.id !== undefined && point.shapeFiles.filter((item) => item.Level2 === '2D Wave Spectra').map((item) => (
              <div key={item.Level2} className={`${styles.menuRow}`}>
                <div className={styles.iconLabel}>
                  <div style={{ width: 20, height: 20, paddingLeft: 16, borderStyle: 'solid', borderColor: item.LegendColor[0], borderWidth: '3px'  }} />
                  <Typography style={{ marginLeft: 5 }}>{item.Level2}</Typography>
                </div>
                <div className={`${styles.groupItem}`}>
                  <div
                    onClick={() => updateShowShape(!item.IsDisplay, point!.dataset, item.Level1, item.Level2, item.LegendColor[0])}
                    style={{ cursor: 'pointer', fontSize: '20px' }}
                  >
                    {item.IsDisplay ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                  </div>
                </div>
              </div>
            ))}
            {point && point?.id && !isHave2DSpectra && (
              <>
                <div className={`${styles.menuRow}`}>
                  <div className={styles.iconLabel}>
                    <div className={styles.boxDisabled} />
                    <Typography style={{ marginLeft: 5, color: 'grey' }}>2D Wave Spectra</Typography>
                  </div>
                  <div className={styles.groupItem}>
                    <div style={{ cursor: 'pointer', fontSize: '20px' }}>
                      <EyeInvisibleOutlined disabled style={{ cursor: 'not-allowed' }} />
                    </div>
                  </div>
                </div>
              </>
            )}
            <div className={`${styles.menuRow}`}>
              <div className={styles.iconLabel}>
                <div className={styles.boxOffshore} />
                <Typography style={{ marginLeft: 5, color: 'black' }}>Offshore Wind Farms</Typography>
              </div>
              <div className={styles.groupItem}>
                <div
                  onClick={() => {setShowMenuOWF(!showMenuOWF)}}
                  style={{ cursor: 'pointer', fontSize: '20px' }}
                >
                  {showMenuOWF ? <UpOutlined style={{fontSize: 15, paddingRight: 1}}  /> : <DownOutlined style={{fontSize: 15, paddingRight: 1}}  /> }
                </div>
              </div>
            </div>
            {showMenuOWF && (
              <div style={{ paddingTop: 1 }}>
                <div className={`${styles.menuRow}`} style={{paddingTop: 0}}>
                  <div className={styles.iconLabel}>
                    <Typography style={{ marginLeft: 40, color: 'black' }}>Polygon</Typography>
                  </div>
                  <div className={styles.groupItem}>
                    <div
                      onClick={() => {setShowGlobalShapeFile(!showGlobalShapeFile)}}
                      style={{ cursor: 'pointer', fontSize: '20px' }}
                    >
                      {showGlobalShapeFile ? <EyeOutlined /> : <EyeInvisibleOutlined/> }
                    </div>
                  </div>
                </div>
                <div className={`${styles.menuRow}`} style={{paddingTop: 0}}>
                  <div className={styles.iconLabel}>
                    <Typography style={{ marginLeft: 40, color: 'black' }}>Name</Typography>
                  </div>
                  <div className={styles.groupItem}>
                    <div
                      onClick={() => {setShowGlobalTextShapeFile(!showGlobalTextShapeFile)}}
                      style={{ cursor: 'pointer', fontSize: '20px' }}
                    >
                      {showGlobalTextShapeFile ? <EyeOutlined /> : <EyeInvisibleOutlined/> }
                    </div>
                  </div>
                </div>
              </div>
            )}
            <Divider style={{ margin: 0 }} />
            <div className={styles.checkboxGroup}>
            {uploadedFeatures.map((layer) => {
              const color = ConvertRGBtoHex(layer.color);
              const isVisible = visibleUploads.includes(layer.id);
              return (
                <div key={layer.id} className={styles.menuRow}>
                  <div className={styles.iconLabel}>
                    <div
                      style={{
                        cursor: 'pointer',
                        width: 20,
                        height: 20,
                        paddingLeft: 16,
                        border:`solid 3px ${color}`,
                      }}
                      onClick={() => changeColor(layer)}
                      />
                    <Tooltip title={layer.id}>
                      <div className={styles.checkboxLabel}>
                        {layer.id}
                      </div>
                    </Tooltip>
                  </div>
                  <div className={`${styles.groupItem}`} style={{ alignItems: 'center'}}>
                    <div style={{ cursor: 'pointer', fontSize: '15px', marginRight: 5 }}>
                      <DeleteOutlined
                        className={styles.deleteUploadIcon}
                        onClick={() => deleteUpload(layer.id)}
                        />
                    </div>
                    <div style={{ cursor: 'pointer', fontSize: '20px' }}>
                      {isVisible ? (
                        <EyeOutlined
                          onClick={() => onChangeUploadVisibility(layer.id)}
                        />
                        ) : (
                          <EyeInvisibleOutlined
                          onClick={() => onChangeUploadVisibility(layer.id)}
                          />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
            <div className={styles.rowUpload}>
              <Upload
                {...uploadProps}
                className={styles.upload}
                fileList={fileList}
              >
                <Tooltip title="The following files must be uploaded together: .shp, .shx, .prj">
                  <Button
                    type="primary"
                    className={styles.uploadButton}
                    loading={uploadingShapefile}
                    onClick={handleOnUpload}
                    size='small'
                  >
                    + Add Shape Files
                    <UploadOutlined />
                  </Button>
                </Tooltip>
              </Upload>
            </div>
          </>
        )}
      </div>

      {/* SURFACE MAP SECTION */}
      {point && point?.id !== undefined && (
        <div className={`${styles.menuBox}`}>
          <div className={styles.wrapperLabel}>
            <div className={styles.menuLabel}>
              Surface Map
            </div>
            {showSurfaceMapSection ? (
              <UpOutlined
              onClick={() => setShowSurfaceMapSection(false)}
              className={styles.toggleArrow}
              />
              ) : (
                <DownOutlined
                onClick={() => setShowSurfaceMapSection(true)}
                className={styles.toggleArrow}
                />
            )}
          </div>
          <div style={{ overflowY: 'auto', maxHeight: '350px' }}>
            {showSurfaceMapSection && (
              <Radio.Group
                className={`${styles.radioGroup}`}
                onChange={(e) => handleSurfaceMap(e.target.value, false)}
                value={
                  point?.surfaceMaps.selectedLevel2
                    ? point.surfaceMaps.selectedLevel1 + '|' + point.surfaceMaps.selectedLevel2
                    : 'none'
                }
              >
                <div className={styles.radioItemNone}>
                  <div className={styles.label}>
                    None
                  </div>
                  <Radio
                    className={styles.radioItem}
                    value={'none'}
                  />
                </div>
                {point!.surfaceMaps.categories.map((c) => {
                  return (
                    <div key={c.name}>
                      <div className={`${styles.level1}`}>{c.name}</div>
                      {point?.surfaceMaps.overlays
                        .filter((o) => o.Level1 === c.name)
                        .map((item) => (
                          <div key={item.Level2} className={styles.radioItemWrapper}>
                            <div className={styles.label}>
                              {item.Level2}
                            </div>
                            <Radio
                              className={styles.radioItem}
                              value={item.Level1 + '|' + item.Level2}
                            />
                          </div>
                        ))}
                    </div>
                  );
                })}
              </Radio.Group>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
